import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import searchIcon from '../assets/search.png';
import filter from '../assets/filter-svgrepo-com.svg';
import download from '../assets/download-minimalistic-svgrepo-com.svg';
import add from '../assets/add-plus-svgrepo-com.svg';
import Product1 from '../components/product1';
import Product2 from '../components/product2';
import Product3 from '../components/product3';
import Product4 from '../components/product4';
import {
  AGENCY_CATEGORIES,
  CATEGORY_LIST,
  CATEGORY_LABELS,
  normalizeCategory,
} from "../constants/agencyCategories";
import { fetchProductsByCategory } from "../services/productService";

const filtersByTab = {
  [AGENCY_CATEGORIES.CLOTHES]: [
    { key: "small", label: "Size: Small" },
    { key: "large", label: "Size: Large" },
  ],
  [AGENCY_CATEGORIES.CARS]: [
    { key: "new", label: "New (2020+)" },
    { key: "old", label: "Old (<2020)" },
  ],
  [AGENCY_CATEGORIES.REAL_ESTATE]: [
    { key: "cheap", label: "Cheap (< 50k)" },
    { key: "expensive", label: "Expensive (50k+)" },
  ],
  [AGENCY_CATEGORIES.WEDDING]: [
    { key: "small", label: "Small (<200 guests)" },
    { key: "large", label: "Large (200+ guests)" },
  ],
};

export default function Products() {
  const [data, setTableData] = useState([]);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filterOptions, setFilterOptions] = useState(
    Object.fromEntries(CATEGORY_LIST.map((c) => [c, ""]))
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedFromURL = queryParams.get("selected");

  const [activeTab, setActiveTab] = useState(AGENCY_CATEGORIES.CLOTHES);
  const navigate = useNavigate();

  const handleNewProduct = () => {
    navigate("/products/add", { state: { fromProducts: true, activeTab } });
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    navigate(`/products?selected=${tab}`);
  };

  useEffect(() => {
    if (selectedFromURL) {
      setActiveTab(normalizeCategory(selectedFromURL));
    }
  }, [selectedFromURL]);

  const fetchProducts = useCallback(async () => {
    try {
      const products = await fetchProductsByCategory(activeTab);
      setTableData(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setTableData([]);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, search, filterOptions]);

  const filteredData = data
    .filter((item) => {
      const searchLower = search.trim().toLowerCase();
      if (!searchLower) return true;
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item._id?.toString().includes(searchLower) ||
        item.id?.toString().includes(searchLower)
      );
    })
    .filter((item) => {
      const currentFilter = filterOptions[activeTab];
      if (!currentFilter) return true;

      if (activeTab === AGENCY_CATEGORIES.CLOTHES) {
        if (currentFilter === "small") return item.size?.toLowerCase() === "s";
        if (currentFilter === "large") return item.size?.toLowerCase() === "l";
      }
      if (activeTab === AGENCY_CATEGORIES.CARS) {
        if (currentFilter === "new") return item.year >= 2020;
        if (currentFilter === "old") return item.year < 2020;
      }
      if (activeTab === AGENCY_CATEGORIES.REAL_ESTATE) {
        if (currentFilter === "cheap") return item.price < 5000;
        if (currentFilter === "expensive") return item.price >= 5000;
      }
      if (activeTab === AGENCY_CATEGORIES.WEDDING) {
        if (currentFilter === "small") return item.capacity < 200;
        if (currentFilter === "large") return item.capacity >= 200;
      }
      return true;
    });

  const tabContents = {
    [AGENCY_CATEGORIES.CLOTHES]: (
      <Product1 category={AGENCY_CATEGORIES.CLOTHES} data={filteredData} onData={setTableData} />
    ),
    [AGENCY_CATEGORIES.CARS]: (
      <Product2 category={AGENCY_CATEGORIES.CARS} data={filteredData} onData={setTableData} />
    ),
    [AGENCY_CATEGORIES.REAL_ESTATE]: (
      <Product3 category={AGENCY_CATEGORIES.REAL_ESTATE} data={filteredData} onData={setTableData} />
    ),
    [AGENCY_CATEGORIES.WEDDING]: (
      <Product4 category={AGENCY_CATEGORIES.WEDDING} data={filteredData} onData={setTableData} />
    ),
  };

  const exportToCSV = async () => {
    try {
      const products = await fetchProductsByCategory(activeTab);
      if (!products?.length) {
        alert(`No products found for ${CATEGORY_LABELS[activeTab]}!`);
        return;
      }

      const headers = [
        "name", "vendorName", "location", "rentalFee", "description", "rating",
        "createdAt", "updatedAt", "subcategory", "size", "brand", "condition",
        "color", "Category", "quantity", "model", "transmission", "fuel", "seats",
        "driver", "area", "bedrooms", "bathrooms", "cameras", "pool", "garden",
        "capacity", "type", "buffet",
      ];

      const formatValue = (val) => {
        if (Array.isArray(val)) return val.join(" | ");
        if (typeof val === "object" && val !== null) return JSON.stringify(val);
        return val ?? "";
      };

      const rows = products.map((p) =>
        headers.map((h) => formatValue(p[h])).join(",")
      );

      const csvContent = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${activeTab}_products.csv`;
      link.click();
    } catch (error) {
      console.error("Export error:", error);
      alert("Error exporting products!");
    }
  };

  const activeLabel = CATEGORY_LABELS[activeTab];

  return (
    <div className="md:p-10 p-4 pb-4 text-base gap-4 dark:bg-agarly-dark">
      <div className="flex md:flex-row flex-col justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">Categories</h1>
          <p className="text-agarly-gray pb-6 text-sm dark:text-gray-400">
            <Link to="/dashboard" className="hover:underline">Dashboard</Link>
            {" / Categories"}
            {activeLabel && (
              <span className="agarly-gradient-text font-bold"> / {activeLabel}</span>
            )}
          </p>
        </div>

        <Link to={`/products/pendingproducts?selected=${activeTab}`}>
          <button className="agarly-btn-secondary mb-6">Pending Products</button>
        </Link>
      </div>

      <div className="agarly-card p-6">
        <div className="flex flex-row items-center justify-between mb-6">
          <div className="hidden sm:block relative w-[40%]">
            <input
              type="text"
              placeholder="Search Product"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="agarly-input pr-10"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <img src={searchIcon} alt="search" className="w-5 h-5" />
            </span>
          </div>

          <div className="flex justify-between gap-2 md:gap-3">
            <div className="relative">
              <button
                onClick={() => setShowFilter(!showFilter)}
                className="agarly-btn-secondary py-2 px-3 text-sm"
              >
                <span className="dark:text-gray-200">Filter</span>
                <img src={filter} alt="" className="w-5 h-5" />
              </button>

              {showFilter && (
                <div className="absolute right-0 mt-2 w-48 agarly-card p-2 z-10">
                  {filtersByTab[activeTab].map((f) => (
                    <button
                      key={f.key}
                      className="block w-full text-left px-2 py-1 rounded-agarly-sm hover:bg-agarly-light dark:hover:bg-agarly-700"
                      onClick={() => {
                        setFilterOptions((prev) => ({ ...prev, [activeTab]: f.key }));
                        setShowFilter(false);
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                  <button
                    className="block w-full text-left px-2 py-1 rounded-agarly-sm hover:bg-agarly-light"
                    onClick={() => {
                      setFilterOptions((prev) => ({ ...prev, [activeTab]: "" }));
                      setShowFilter(false);
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              )}
            </div>

            <button onClick={exportToCSV} className="agarly-btn-secondary py-2 px-3 text-sm">
              Export
              <img src={download} alt="" className="w-5 h-5" />
            </button>

            <button type="button" onClick={handleNewProduct} className="agarly-btn-primary text-sm">
              New Product
              <img src={add} alt="" className="w-5 h-5" />
            </button>
          </div>
        </div>

        {filterOptions[activeTab] && (
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-1 bg-agarly-light text-agarly-dark px-2 py-1 rounded-full text-xs">
              {filtersByTab[activeTab].find((f) => f.key === filterOptions[activeTab])?.label}
              <button
                onClick={() => setFilterOptions((prev) => ({ ...prev, [activeTab]: "" }))}
                className="ml-1 font-bold"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="flex py-1 px-1 md:space-x-2 mb-4 border border-agarly-surface rounded-agarly-lg justify-between gap-1">
          {CATEGORY_LIST.map((tab) => (
            <button
              key={tab}
              className={`py-2 flex-1 text-sm rounded-agarly-md transition-colors ${
                activeTab === tab
                  ? "bg-agarly-light text-agarly-dark font-semibold"
                  : "text-agarly-gray hover:bg-agarly-light/50 dark:text-gray-300 dark:hover:bg-agarly-700"
              }`}
              onClick={() => handleTabClick(tab)}
            >
              {CATEGORY_LABELS[tab]}
            </button>
          ))}
        </div>

        <div>{tabContents[activeTab]}</div>
      </div>
    </div>
  );
}
